import { t } from '@hefe/i18n';
import { Task, TaskableType, TaskableTypeEnum } from '@hefe/models';
import { ModalsSlice, TasksActions, currentUserSelector } from '@hefe/store';
import { useMemo, useState } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import CustomTable from '../../../core/components/table/custom-table.component';
import AddEditClubTaskForm from '../../forms/add-edit-club-task/add-edit-club-task.form';
import AddEditTeamTaskForm from '../../forms/add-edit-team-task/add-edit-team-task.form';

interface TasksListComponentProps {
  tasks: Task[];
  type: TaskableTypeEnum;
}

export default function TasksListComponent(props: TasksListComponentProps) {
  const dispatch = useAppDispatch();

  const user = useSelector(currentUserSelector);
  const [deleting, setDeleting] = useState(false);

  const tableName = useMemo(() => {
    switch (props.type) {
      case TaskableType.CLUB:
        return 'club_tasks';
      case TaskableType.BASKETBALL_TEAM:
        return 'basketball_team_tasks';
      default:
        return 'tasks';
    }
  }, [props.type]);

  const addTaskBtnDisabledTooptip = useMemo(() => {
    const tasksLimit = user?.club_subscription?.limits.tasks;

    if (tasksLimit && props.tasks.length + 1 > tasksLimit) {
      return t('errors.permissions.tasks');
    }

    return undefined;
  }, [user?.club_subscription?.limits.tasks, props.tasks.length]);

  const addForm = useMemo(() => {
    switch (props.type) {
      case TaskableType.CLUB:
        return {
          title: t('tasks.create'),
          formId: 'add-edit-club-task-form-new',
          component: <AddEditClubTaskForm />,
          width: 800,
          disabledTooltip: addTaskBtnDisabledTooptip,
        };
      case TaskableType.BASKETBALL_TEAM:
        return {
          title: t('tasks.create'),
          formId: 'add-edit-team-task-form-new',
          component: <AddEditTeamTaskForm />,
          width: 800,
        };
      default:
        return undefined;
    }
  }, [addTaskBtnDisabledTooptip, props.type]);

  const deleteTask = (taskId: number) => {
    const task = props.tasks.find((t) => t.id === taskId);
    if (task) {
      setDeleting(true);
      dispatch(TasksActions.deleteTask(task))
        .unwrap()
        .finally(() => setDeleting(false));
    }
  };

  const openEditModal = (task: Task) => {
    switch (props.type) {
      case TaskableType.CLUB:
        dispatch(
          ModalsSlice.modalsActions.openModal({
            component: <AddEditClubTaskForm task={task} />,
            title: task.name,
            formId: `add-edit-club-task-form-${task.id}`,
            width: 800,
          })
        );
        break;
      case TaskableType.BASKETBALL_TEAM:
        dispatch(
          ModalsSlice.modalsActions.openModal({
            component: <AddEditTeamTaskForm task={task} />,
            title: task.name,
            formId: `add-edit-team-task-form-${task.id}`,
            width: 800,
          })
        );
        break;
    }
  };

  return (
    <CustomTable
      name={tableName}
      rowKey="id"
      rowSelection
      onRowClick={(item) => openEditModal(item)}
      addForm={addForm}
      deleteAction={{
        action: (keys: any[]) => (keys[0] ? deleteTask(keys[0]) : null),
        loading: deleting,
      }}
      columns={[
        {
          key: 'name',
          title: t('name'),
          dataIndex: 'name',
          sortType: 'string',
          default: true,
        },
        {
          key: 'home_games',
          title: t('at_home_games'),
          dataIndex: 'at_home_games',
          default: true,
          render: (value) => (value ? <FaCheck id="fa-check" /> : <FaTimes />),
        },
        {
          key: 'away_games',
          title: t('at_away_games'),
          dataIndex: 'at_away_games',
          default: true,
          render: (value) => (value ? <FaCheck id="fa-check" /> : <FaTimes />),
        },
        {
          key: 'practices',
          title: t('at_practices'),
          dataIndex: 'at_practices',
          default: true,
          render: (value) => (value ? <FaCheck id="fa-check" /> : <FaTimes />),
        },
        {
          key: 'min_users',
          title: t('min_num_of_users'),
          dataIndex: 'min_num_of_users',
          default: true,
          render: (value) => value ?? '0',
        },
        {
          key: 'max_users',
          title: t('max_num_of_users'),
          dataIndex: 'max_num_of_users',
          default: true,
          render: (value) => value ?? '∞',
        },
        {
          key: 'is_public',
          title: t('is_public'),
          dataIndex: 'is_public',
          default: true,
          render: (value) => (value ? <FaCheck id="fa-check" /> : <FaTimes />),
        },
        {
          key: 'requires_presence',
          title: t('tasks.requires_presence.title'),
          dataIndex: 'requires_presence',
          default: true,
          render: (value) => (value ? <FaCheck id="fa-check" /> : <FaTimes />),
        },
        {
          key: 'allow_own_assignments',
          title: t('tasks.own_assignments.title'),
          dataIndex: 'allow_own_assignments',
          default: true,
          render: (value) => (value ? <FaCheck id="fa-check" /> : <FaTimes />),
        },
        {
          key: 'description',
          title: t('note'),
          dataIndex: 'description',
          default: false,
        },
      ]}
      dataSource={props.tasks}
    />
  );
}
